import clsx from "clsx";
import SelectCombo from "components/Forms/SelectCombo/SelectCombo";
import CompanyNavLink from "components/navigation/CompanyNavLink";
import DisplayIfUserType from "components/utils/DisplayIfUserType/DisplayIfUserType";
import type React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { Tab, TabGroupInterfaceProps } from "./model";

export default function TabSwitchButtonGroup(
  props: TabGroupInterfaceProps,
): React.ReactElement {
  const { tabs, liveTab } = props;
  const navigate = useNavigate();
  const location = useLocation();

  const tabMap: Map<string, Tab> = new Map(
    Object.entries(
      tabs.reduce((accumulator, tab) => {
        return { ...accumulator, [tab.tabPath]: tab };
      }, {}),
    ),
  );

  return (
    <div data-cy={"tab-interface"}>
      <div className="md:hidden">
        <label htmlFor="tabs" className="sr-only">
          Select a tab
        </label>
        <SelectCombo
          options={tabs.map((v) => {
            return {
              value: v.tabPath,
              label: v.tabName,
              disabled: v.disabled,
            };
          })}
          name={"tab"}
          onChange={(v) => {
            const tab = tabMap.get(v as string) as Tab;
            if (tab.disabled) return;

            props.switchTab
              ? props.switchTab(tab)
              : navigate({
                  pathname: tab.tabPath,
                  search: tab.keepSearchString ? location.search : undefined,
                });
          }}
          value={liveTab?.tabPath || tabs[0].tabPath}
        />
      </div>
      <div className="hidden md:block pb-3">
        <div className="border-b border-gray-200">
          <nav
            aria-label="Tabs"
            className="-mb-px flex space-x-4 lg:space-x-8 "
          >
            {tabs.map((tab) => (
              <DisplayIfUserType
                userTypes={tab.userTypes || "viewer"}
                key={tab.tabPath}
              >
                {tab.disabled ? (
                  <span
                    className={clsx(
                      tab.tabPath === liveTab?.tabPath
                        ? "border-gray-300 text-gray-300"
                        : "border-transparent text-gray-300",
                      "whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium cursor-not-allowed",
                    )}
                    data-cy={`tab-${tab.tabName}`}
                    aria-disabled="true"
                  >
                    {tab.tabName}
                  </span>
                ) : (
                  <CompanyNavLink
                    to={
                      props.switchTab
                        ? { hash: "#" }
                        : {
                            pathname: tab.tabPath,
                            search: tab.keepSearchString
                              ? location.search
                              : undefined,
                          }
                    }
                    aria-current={
                      tab.tabPath === liveTab?.tabPath ? "page" : undefined
                    }
                    className={clsx(
                      tab.tabPath === liveTab?.tabPath
                        ? "border-brand-dark-700 text-brand-dark-700"
                        : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700",
                      "whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium",
                    )}
                    onClick={() => {
                      props.switchTab?.(tab);
                    }}
                    dataCy={`tab-${tab.tabName}`}
                  >
                    {tab.tabName}
                  </CompanyNavLink>
                )}
              </DisplayIfUserType>
            ))}
          </nav>
        </div>
      </div>
    </div>
  );
}
